import { useMediaQuery } from 'react-responsive'

import { EXPERIMENT_NAME } from 'experiments/constants/Experiments'
import {
  useExperimentState,
  useLogExperiment,
  useStartExperiment,
} from 'experiments/hooks/useExperiment'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useProductTemplateContext } from 'productTemplate/components/ProductTemplateContextProvider'
import { ProductQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { sizes } from 'shared/lib/media'

import { t } from 'localization'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import PaymentPromotional from 'productTemplate/components/PaymentPromotional'
import { getProductSizeCategory } from 'productTemplate/utils/getProductSizeCategory'
import {
  getDiscountPercentage,
  MINIMUM_DISCOUNT_PERCENTAGE_TO_DISPLAY_UNDER_RETAIL,
} from 'productTemplate/utils/under-retail-utils'
import { useEffect, useState } from 'react'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { ISizePickerOption } from 'shared/types/ISizePickerOption'
import OutOfStockNotice from './OutOfStockNotice'
import {
  ProductPickerWrapper,
  SizingLabel,
  StyledSizeGuide,
  TopLabelSection,
} from './ProductPickerStyles'
import ProductSideSheet from './ProductSideSheet'
import { ShoppingRegionSelector } from './ShoppingRegionSelector'
import SizePickerDesktop from './SizePickerDesktop'
import SizePickerMobile from './SizePickerMobile'

const TP = 'productTemplate.components.ProductPicker.ProductPicker'

type ProductPickerProps = {
  isLoading: boolean
}

export const ProductPicker = ({ isLoading }: ProductPickerProps) => {
  const regionSelectorPlacement: EXPERIMENT_NAME = 'web_fc_fully_landed_pricing_copy_placement_test'
  const underRetailExperiment: EXPERIMENT_NAME = 'web_fc_under_retail'
  const enablePricingCopyPlacementExperiment = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_FULLY_LANDED_PRICING_COPY_EXPERIMENT,
  )
  const enableExperiments = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_EXPERIMENTS)
  const enableUnderRetailExperiment = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_UNDER_RETAIL_EXPERIMENT,
  )
  useStartExperiment(regionSelectorPlacement, !enablePricingCopyPlacementExperiment)
  useStartExperiment(underRetailExperiment, !enableExperiments || !enableUnderRetailExperiment)
  useLogExperiment(regionSelectorPlacement, !enablePricingCopyPlacementExperiment)
  const { currencyCode, country: selectedCountryCode } = useShoppingRegionContext()
  const isShoppingRegionUS = selectedCountryCode === 'US'
  const [enableLogUnderRetailExperiment, setEnableLogUnderRetailExperiment] = useState(false)
  // const logUnderRetailExposure = () => setEnableLogUnderRetailExperiment(true)
  useLogExperiment(
    underRetailExperiment,
    !enableExperiments ||
      !enableUnderRetailExperiment ||
      !enableLogUnderRetailExperiment ||
      !isShoppingRegionUS,
  )
  const experimentState = useExperimentState()
  const isMobile = useMediaQuery({ maxWidth: sizes.large })
  const {
    productTemplate: { sizeCategory, sizePickerOptions, slug, localizedSpecialDisplayPriceCents },
    selectedSize,
    isApparel,
    setSize,
  } = useProductTemplateContext()

  // FC2-1703 - temporarily hide size guide
  const showSizeGuide = false
  // const showSizeGuide = productCategory ? productCategory === 'shoes' : true

  const isMarketPricing = useFeatureFlag(FeatureFlag.MARKET_PRICING)

  useEffect(() => {
    let shouldLogUnderRetailExperiment = false
    if (selectedSize) {
      const discountPercentage = getDiscountPercentage(
        localizedSpecialDisplayPriceCents?.value || 0,
        selectedSize.new?.lowestPriceCents?.value || 0,
      )
      shouldLogUnderRetailExperiment =
        discountPercentage !== null &&
        discountPercentage > MINIMUM_DISCOUNT_PERCENTAGE_TO_DISPLAY_UNDER_RETAIL
    }

    setEnableLogUnderRetailExperiment(shouldLogUnderRetailExperiment)
  }, [selectedSize])

  const handleSizeOptionClick = (option: ISizePickerOption) => {
    setSize(option)
    sendTrackingEvent('PDP_SIZE_TAP', {
      product_template_slug: slug,
      size: option.size.display,
      under_retail:
        isApparel ||
        (option.new?.lowestPriceCents?.value || -Infinity) < localizedSpecialDisplayPriceCents.value
          ? true
          : false,
      price: option.new?.lowestPriceCents?.value || '',
      currency_code: currencyCode,
      new_standard_price: option.new?.lowestPriceCents?.value || '',
      used_price: option.used?.lowestPriceCents?.value || '',
    })
  }

  const lowestPrice = (() => {
    if (isMarketPricing) {
      if (
        selectedSize?.new?.lowestPriceCents?.localizedValue <
        (selectedSize?.new?.instantShipLowestPriceCents?.localizedValue || Infinity)
      ) {
        return selectedSize?.new?.lowestPriceCents?.localizedValue
      }
      return selectedSize?.new?.instantShipLowestPriceCents?.localizedValue
    }
    return selectedSize?.new?.instantShipLowestPriceCents?.localizedValue
  })()

  const sizeLabel = () => {
    switch (sizeCategory) {
      case 'youth':
      case 'infant':
        return t(`${TP}.buyNewSectionLabelYouth`, {
          defaultValue: 'Select US {sizeCategory} Size',
          sizeCategory: getProductSizeCategory(sizeCategory),
        })
      default:
        return isApparel
          ? t(`${TP}.buyNewSectionApparelLabel`, {
              defaultValue: `Select US Size`,
            })
          : t(`${TP}.buyNewSectionLabel`, {
              defaultValue: `Select US {sizeCategory}'s Size`,
              sizeCategory: `${getProductSizeCategory(sizeCategory)}`,
            })
    }
  }

  if (!sizePickerOptions || !sizePickerOptions.length) {
    return (
      <ProductPickerWrapper>
        <OutOfStockNotice />
      </ProductPickerWrapper>
    )
  }

  return (
    <ProductPickerWrapper>
      <ProductSideSheet />
      <TopLabelSection $isMobile={isMobile}>
        {!isMobile && (
          <SizingLabel data-qa={ProductQA.BuyNewSectionLabel}>{sizeLabel()}</SizingLabel>
        )}
        {showSizeGuide && (
          <div>
            <StyledSizeGuide />
          </div>
        )}
      </TopLabelSection>
      {isMobile ? (
        <SizePickerMobile
          sizePickerOptions={sizePickerOptions}
          handleSizeOptionClick={handleSizeOptionClick}
          isButtonRowVisible={!isLoading}
        />
      ) : (
        <SizePickerDesktop
          sizePickerOptions={sizePickerOptions}
          handleSizeOptionClick={handleSizeOptionClick}
          isButtonRowVisible={!isLoading}
        />
      )}
      <PaymentPromotional
        lowestPrice={lowestPrice}
        amount={selectedSize?.new?.lowestPriceCents?.value}
      />
      {enableExperiments && enableUnderRetailExperiment && enablePricingCopyPlacementExperiment ? (
        experimentState[regionSelectorPlacement]?.groupAssigned === 'control' && (
          <ShoppingRegionSelector />
        )
      ) : (
        <ShoppingRegionSelector />
      )}
    </ProductPickerWrapper>
  )
}
